// 主人公の移動 4 版目 (2026-10-13・DEV ?lunaWalk=v4run|v4walk) の脚と腰。 配布版は変えない。
//   3 版目までは脚と腰を式 (cos の波・一定の速さで流れる足) で作り、3 回続けて「不自然」と言われた
//   (2 版目「Spine 特有の不自然な歩き」・3 版目「中腰くらいですり足」「せかせか」「腰も低い」)。
//   4 版目は作り方を変える: 見本のコマから読んだ鍵の姿勢 (太ももとすねの角度・足の角度・腰の高さ) を並べ、
//   鍵の間を滑らかな曲線 (Catmull-Rom) でつなぐ。 床に着いている間だけは、床に着いた点 (踵か爪先) を床に留める
//   (足が滑らない) ために足首の位置を計算で出す。 記録 = docs/research/2026-10-13-lunaire-walk-v4.md
//
//   v4run  = 見本 R1 (ZZZ 星見雅の走り) を写した走り。 両足が浮く間で進み、踏み出す足は膝を上げ、蹴った足は踵を上げる。
//   v4walk = 見本 Z3 (ZZZ 刀の少女の歩き)・Z1 (ベル) を写した歩き。 踵から着いて爪先で蹴る (足の転がり)。
//
//   脚の長さの問題 (3 版目の申し送り「腰を上げると脚が足に届かない」): 立ち姿の脚はほぼ伸び切っている (余り 0.5px)。
//   足首を前後にずらすと必ず腰が下がる。 4 版目は「足の転がり」で稼ぐ: 着地は踵を支点に爪先を上げ、蹴る時は爪先を支点に
//   踵を上げる。 足首が床から上がるぶん・前後へ寄るぶん、腰を下げずに足が前後へ届く (靴が高いので効きが大きい)。
//   それでも届かない時は、その瞬間だけ腰を届く高さまで下げる (hipClamp・測って記録に書く)。
//
//   このファイルは描画にもゲームの判定にも使う = Spine を import しない (テストから読める)。

/** Spine の 1 単位 (setup の大きさ・src/spine_player.ts と同じ 120px / 1581.73) */
const S = 120 / 1581.7297
/** 根 (root) の translate (Spine 単位) → 画面 (px・右向き・上 +) の行列 [[ax, bx], [ay, by]] (tmp で spine-core から測った値) */
const RA = 0.0758289, RB = 0.0023814
export function unitsToWorld(x: number, y: number): { x: number, y: number } { return { x: -RA * x - RB * y, y: -RB * x + RA * y } }
export function worldToUnits(dx: number, dy: number): { x: number, y: number } {
  const d = RA * RA + RB * RB
  return { x: -(RA * dx + RB * dy) / d, y: (RA * dy - RB * dx) / d }
}

/** 片脚の setup の形 (画面 px・右向き・上 +・足元 = 0)。 tests/player_walk_new.test.ts が skeleton と一致を確かめる */
export interface LegGeom {
  /** 太ももの付け根 */ thigh: { x: number, y: number }
  /** 足首 (足の的・靴の上端) */ ankle: { x: number, y: number }
  /** 太もも (付け根→膝)・すね (膝→足首) の長さ (px) */ l1: number, l2: number
  /** 足首から見た爪先・踵 (靴の絵の下の前の角・後ろの角) */ toe: { x: number, y: number }, heel: { x: number, y: number }
  /** setup のすねの向き (度・真下から前へ +) */ shin0: number
}
export const LEG: Record<'front' | 'back', LegGeom> = {
  front: { thigh: { x: -5.946, y: 64.488 }, ankle: { x: -8.432, y: 15.857 }, l1: 403.52 * S, l2: 245.235 * S, toe: { x: 11.54, y: -15.56 }, heel: { x: -4.62, y: -15.50 }, shin0: -13.7 },
  back: { thigh: { x: 3.906, y: 64.488 }, ankle: { x: 3.834, y: 20.188 }, l1: 345.60 * S, l2: 245.235 * S, toe: { x: 12.14, y: -15.10 }, heel: { x: -4.01, y: -15.67 }, shin0: -10.6 },
}

// ---- 滑らかな曲線 (鍵を必ず通る・周期あり/なし) ----
export interface Key { t: number, v: number }
/** 周期 1 の鍵 (t = 0〜1) を Catmull-Rom (時間の間隔が不揃いでも使える Hermite) でつなぐ */
export function periodicSpline(keys: readonly Key[], t: number): number {
  const n = keys.length
  const w = ((t % 1) + 1) % 1
  let i = n - 1
  for (let k = 0; k < n; k++) if (keys[k]!.t <= w) i = k
  const k0 = keys[(i - 1 + n) % n]!, k1 = keys[i]!, k2 = keys[(i + 1) % n]!, k3 = keys[(i + 2) % n]!
  const t0 = k0.t - (i - 1 < 0 ? 1 : 0), t1 = k1.t
  const t2 = k2.t + (i + 1 >= n ? 1 : 0), t3 = k3.t + (i + 2 >= n ? 1 : 0)
  const ww = w < t1 ? w + 1 : w
  return hermite(k1.v, k2.v, (k2.v - k0.v) / (t2 - t0), (k3.v - k1.v) / (t3 - t1), t1, t2, ww)
}
function hermite(p0: number, p1: number, m0: number, m1: number, t0: number, t1: number, t: number): number {
  const h = t1 - t0
  const s = (t - t0) / h
  const s2 = s * s, s3 = s2 * s
  return (2 * s3 - 3 * s2 + 1) * p0 + (s3 - 2 * s2 + s) * h * m0 + (-2 * s3 + 3 * s2) * p1 + (s3 - s2) * h * m1
}
/** 端の傾きを決めた鍵の列 (t 昇順・端を含む) を Hermite でつなぐ */
function openSpline(ts: number[], vs: number[], m0: number, m1: number, t: number): number {
  const n = ts.length
  let i = 0
  while (i < n - 2 && t > ts[i + 1]!) i++
  const tan = (k: number) => (k === 0 ? m0 : k === n - 1 ? m1 : (vs[k + 1]! - vs[k - 1]!) / (ts[k + 1]! - ts[k - 1]!))
  return hermite(vs[i]!, vs[i + 1]!, tan(i), tan(i + 1), ts[i]!, ts[i + 1]!, t)
}

// ---- 足運びの決まり ----
export interface SwingKey {
  /** 浮いている間の位置 (0 = 爪先が離れた瞬間・1 = 次に着く瞬間) */ u: number
  /** 太ももの角度 (度・真下から前へ +)・膝の曲げ (度・+ = すねが後ろへ折れる)・足首の伸ばし (度・+ = 爪先が下がる) */
  thigh: number, knee: number, pf: number
}
export interface V4Gait {
  /** 進む速さ (px/秒) */ speedPxPerSec: number
  /** 1歩の時間 (秒) */ stepSec: number
  /** 片足が床に居る割合 (1周の)。 0.5 未満 = 両足が浮く間がある (走り)・0.5 より上 = 両足が着く間がある (歩き) */ stanceFrac: number
  /** 着地の瞬間、足が平らなら足首がどこにあるか (立ち姿の足首から前へ px)。 床に居る間に体が進むぶん後ろへ流れる */ contactFwdPx: number
  /** 床に居る間の足の角度 (度・+ = 踵が上がる [爪先が支点]・− = 爪先が上がる [踵が支点])。 s = 床に居る間の位置 0〜1 */
  stance: readonly { s: number, foot: number }[]
  /** 浮いている間の鍵 (端の 0 と 1 は床の姿から自動でつなぐので入れない) */ swing: readonly SwingKey[]
  /** 腰の上下 (Spine 単位・立ち姿の setup = 0)。 ψ = 1歩の中の位置 (0 = 手前の足が着いた瞬間) */ hip: readonly Key[]
  /** 後ろへ歩く時、浮いている足の膝と太ももを振る量の割合 (1 = 前へと同じ) */ backSwingScale: number
}

export interface V4LegState {
  /** 足の的の translate (Spine 単位・setup からの差) */ ik: { x: number, y: number }
  /** 足の骨の回転 (度・setup からの差・+ = 爪先が下がる) */ foot: number
  stance: boolean
  /** 画面 px (足首・膝・太ももの付け根) 測る用 */ ankle: { x: number, y: number }, knee: { x: number, y: number }, thigh: { x: number, y: number }
}
export interface V4Pose {
  front: V4LegState, back: V4LegState
  /** 腰の上下 (Spine 単位) */ hipY: number
  /** 鍵の腰から、脚を届かせるために下げた量 (Spine 単位・0 = 下げていない) */ hipClamp: number
}

export function v4CycleSec(G: V4Gait): number { return 2 * G.stepSec }
export function v4StepPx(G: V4Gait): number { return G.speedPxPerSec * G.stepSec }

const rad = (d: number) => d * Math.PI / 180
const wrap01 = (p: number) => ((p % 1) + 1) % 1
const rot = (v: { x: number, y: number }, deg: number) => {
  // 画面で時計回り (右向きの人形で + = 爪先が下がる) に deg 回す
  const c = Math.cos(rad(deg)), s = Math.sin(rad(deg))
  return { x: c * v.x + s * v.y, y: -s * v.x + c * v.y }
}

function stanceFoot(G: V4Gait, s: number): number {
  const ks = G.stance
  if (s <= ks[0]!.s) return ks[0]!.foot
  for (let i = 0; i < ks.length - 1; i++) {
    const a = ks[i]!, b = ks[i + 1]!
    if (s <= b.s) {
      const k = (s - a.s) / (b.s - a.s)
      return a.foot + (b.foot - a.foot) * k * k * (3 - 2 * k)   // 鍵の間はゆっくり始まりゆっくり止まる
    }
  }
  return ks[ks.length - 1]!.foot
}

/** 床に居る間の足首 (画面 px・体に対して)。 s = 0〜1。 足が平らな時の足首は一定の速さで後ろへ流れ、支点 (踵か爪先) は床に留まる */
function stanceAnkle(G: V4Gait, L: LegGeom, s: number): { x: number, y: number, foot: number } {
  const sweep = G.speedPxPerSec * G.stanceFrac * v4CycleSec(G)
  const flatX = L.ankle.x + G.contactFwdPx - sweep * s
  const a = stanceFoot(G, s)
  // 支点: + (踵が上がる) = 爪先・− (爪先が上がる) = 踵。 支点は足が平らな時の位置のまま (= 床に対して止まっている)
  const pivotOff = a >= 0 ? L.toe : L.heel
  const pivot = { x: flatX + pivotOff.x, y: L.ankle.y + pivotOff.y }
  const back = rot({ x: -pivotOff.x, y: -pivotOff.y }, a)
  return { x: pivot.x + back.x, y: pivot.y + back.y, foot: a }
}

/** 2 本の骨で足首に届く膝 (画面 px)。 膝は前 (+x) へ曲がる */
export function solveKnee(L: LegGeom, o: { x: number, y: number }, p: { x: number, y: number }): { knee: { x: number, y: number }, reach: number } {
  const dx = p.x - o.x, dy = p.y - o.y
  const d = Math.hypot(dx, dy)
  const dd = Math.min(d, L.l1 + L.l2 - 1e-6)
  const a = Math.acos(Math.max(-1, Math.min(1, (L.l1 * L.l1 + dd * dd - L.l2 * L.l2) / (2 * L.l1 * dd))))
  const base = Math.atan2(dy, dx)
  // 右向きで膝を前へ: 付け根→足首の向きから反時計回り (下向きの脚なら前) へ a
  const ang = base + a
  return { knee: { x: o.x + L.l1 * Math.cos(ang), y: o.y + L.l1 * Math.sin(ang) }, reach: d }
}
export const angFromDown = (from: { x: number, y: number }, to: { x: number, y: number }) => Math.atan2(to.x - from.x, -(to.y - from.y)) * 180 / Math.PI

/**
 * 4 版目の脚と腰 (位相 phase 0〜1・1周で2歩)。 dir = 1 前へ・−1 後ろへ (後ろへは時間を逆に流し、浮いている足の振りを backSwingScale へ小さくする)。
 *   位相 0 = 手前の足 (front) が着いた瞬間。 奥の足 (back) は半周ずれる。
 */
export function v4Pose(G: V4Gait, phase: number, dir: 1 | -1 = 1): V4Pose {
  const p = wrap01(dir === 1 ? phase : -phase)
  const hipKey = periodicSpline(G.hip, 2 * p)
  const legs = { front: legAt(G, LEG.front, p, hipKey, dir), back: legAt(G, LEG.back, wrap01(p + 0.5), hipKey, dir) }
  // 床に居る足が届かない時だけ、その瞬間の腰を届く高さまで下げる (0.1px の余り)
  let hip = hipKey
  for (const side of ['front', 'back'] as const) {
    const st = legs[side]
    if (!st.stance) continue
    const L = LEG[side]
    const max = maxHipForAnkle(L, st.ankle, 0.1)
    if (max < hip) hip = max
  }
  const front = hip === hipKey ? legs.front : legAt(G, LEG.front, p, hip, dir)
  const back = hip === hipKey ? legs.back : legAt(G, LEG.back, wrap01(p + 0.5), hip, dir)
  return { front, back, hipY: hip, hipClamp: hipKey - hip }
}

/** 腰の上下 (単位) で太ももの付け根が動く量 (画面 px) */
export function thighAt(L: LegGeom, hipY: number) { const d = unitsToWorld(0, hipY); return { x: L.thigh.x + d.x, y: L.thigh.y + d.y } }
/** 足首 a (画面 px) に脚が届く一番高い腰 (単位)。 margin = 伸ばし切らない余り (px) */
export function maxHipForAnkle(L: LegGeom, a: { x: number, y: number }, margin: number): number {
  const R = L.l1 + L.l2 - margin
  // 腰を上げると付け根は (−RB, RA)×y 動く。 |付け根 − 足首| = R になる y を二分法で探す
  let lo = -400, hi = 400
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2
    const o = thighAt(L, mid)
    if (Math.hypot(o.x - a.x, o.y - a.y) <= R) lo = mid
    else hi = mid
  }
  return lo
}

function legAt(G: V4Gait, L: LegGeom, q: number, hipY: number, dir: 1 | -1): V4LegState {
  // q = この足の位相 (0 = 着地)
  const f = G.stanceFrac
  const o = thighAt(L, hipY)
  let ankle: { x: number, y: number }, foot: number
  const stance = q < f
  if (stance) {
    const st = stanceAnkle(G, L, q / f)
    ankle = { x: st.x, y: st.y }; foot = st.foot
  } else {
    const u = (q - f) / (1 - f)
    const sw = swingAnkle(G, L, u, hipY, dir)
    ankle = sw.ankle; foot = sw.foot
  }
  const { knee } = solveKnee(L, o, ankle)
  const w = worldToUnits(ankle.x - L.ankle.x, ankle.y - L.ankle.y)
  return { ik: w, foot, stance, ankle, knee, thigh: o }
}

/** 鍵の太もも・膝の角度から足首の位置 (画面 px・付け根から) */
function fk(L: LegGeom, o: { x: number, y: number }, thigh: number, knee: number) {
  const k = { x: o.x + L.l1 * Math.sin(rad(thigh)), y: o.y - L.l1 * Math.cos(rad(thigh)) }
  const sh = thigh - knee
  return { x: k.x + L.l2 * Math.sin(rad(sh)), y: k.y - L.l2 * Math.cos(rad(sh)) }
}

/** 浮いている間の足首と足の角度。 u = 0 (爪先が離れた) 〜 1 (次に着く)。 端は床の姿と位置も速さもつながる */
function swingAnkle(G: V4Gait, L: LegGeom, u: number, hipY: number, dir: 1 | -1) {
  const o = thighAt(L, hipY)
  const eps = 1e-3
  const end0 = stanceAnkle(G, L, 1), end0b = stanceAnkle(G, L, 1 - eps)
  const end1 = stanceAnkle(G, L, 0), end1b = stanceAnkle(G, L, eps)
  // 床の間の速さ (位置 / s) を浮いている間の u の速さへ直す (時間の長さの比)
  const k = (1 - G.stanceFrac) / G.stanceFrac
  const m0 = { x: (end0.x - end0b.x) / eps * k, y: (end0.y - end0b.y) / eps * k }
  const m1 = { x: (end1b.x - end1.x) / eps * k, y: (end1b.y - end1.y) / eps * k }
  const sc = dir === 1 ? 1 : G.backSwingScale
  const keys = G.swing.map((s) => ({ u: s.u, a: fk(L, o, s.thigh * sc, s.knee * sc), pf: s.pf * sc }))
  const ts = [0, ...keys.map((s) => s.u), 1]
  const xs = [end0.x, ...keys.map((s) => s.a.x), end1.x]
  const ys = [end0.y, ...keys.map((s) => s.a.y), end1.y]
  const ankle = { x: openSpline(ts, xs, m0.x, m1.x, u), y: openSpline(ts, ys, m0.y, m1.y, u) }
  // 曲線が脚の届く外へふくらんだ所 (爪先が離れた直後) は付け根へ引き寄せる (脚と靴の間にすき間を出さない)
  const dx = ankle.x - o.x, dy = ankle.y - o.y, d = Math.hypot(dx, dy), R = L.l1 + L.l2 - 0.1
  if (d > R) { ankle.x = o.x + dx * R / d; ankle.y = o.y + dy * R / d }
  // 足の角度 = すねに沿わせた角度 + 足首の伸ばし (鍵)。 端は床の時の角度につなぐ
  const { knee } = solveKnee(L, o, ankle)
  const shin = angFromDown(knee, ankle)
  const shinAt = (a: { x: number, y: number }) => angFromDown(solveKnee(L, o, a).knee, a)
  const pf0 = end0.foot + (shinAt(end0) - L.shin0)
  const pf1 = end1.foot + (shinAt(end1) - L.shin0)
  const pf = openSpline(ts, [pf0, ...keys.map((s) => s.pf), pf1], 0, 0, u)
  return { ankle, foot: -(shin - L.shin0) + pf }
}

// ---- 2 つの足運び ----
/**
 * v4run: 見本 R1 (ZZZ 星見雅・武器を持った走り・60 コマ/秒・1歩 0.25 秒) のコマから読んだ鍵 (目で読んだ・±10° ほど)。
 *   R1 は背丈の約 5 個/秒の全力の走りで、床に居る足は大きく前後へ開く (着地 +25°・蹴り −40°)。 160px/秒 (背丈 1.33 個/秒) で
 *   同じ開きにすると腰が 10px 以上沈むので、床の間の脚の角度は写さず、時間の割合 (片足が床 R1 は 0.27 周・ここは 0.25 周)・足の角度 (着地はほぼ平ら・
 *   蹴りで爪先立ち)・浮いている脚の形 (蹴った脚が後ろへ上がる → 踵が尻へ → 膝を前へ → 伸ばして着く) と腰の上下の時機を写した。
 *   1歩は 0.33 秒 (R1 の 0.25 秒の歩数だと 1歩 40px のちょこちょこ・3 版目の 0.36 秒は「せかせか」と言われた。 歩数でなく脚の振りの小ささと腰の低さが元と見て、間を取った)。
 */
export const V4_RUN: V4Gait = {
  speedPxPerSec: 160,
  stepSec: 0.33,
  stanceFrac: 0.25,
  contactFwdPx: 7,
  // 着地は爪先寄りで踵がわずかに浮き (R1 #0)、すぐ平ら・後半で踵が上がり爪先で蹴る (R1 #8 は靴が縦に近い)
  stance: [{ s: 0, foot: 8 }, { s: 0.22, foot: 0 }, { s: 0.45, foot: 0 }, { s: 1, foot: 24 }],
  // 浮いている間: R1 #11 (蹴った脚が後ろへ上がる)・#15 (踵が尻へ)・#19 (たたんだまま前へ)・#23 (膝が前へ)・#27 (伸ばして着地へ)
  swing: [
    { u: 0.14, thigh: -24, knee: 38, pf: 28 },
    { u: 0.32, thigh: -18, knee: 68, pf: 22 },
    { u: 0.50, thigh: 6, knee: 78, pf: 16 },
    { u: 0.68, thigh: 30, knee: 58, pf: 10 },
    { u: 0.86, thigh: 26, knee: 16, pf: 4 },
  ],
  // 腰 (1歩の中): 着地の後に沈み (R1 の頭は着地の 5 コマ後 = 1歩の 0.33 で一番低い)、浮いている間に上がる (0.83 で一番高い)
  hip: [{ t: 0, v: 16 }, { t: 0.12, v: 4 }, { t: 0.27, v: -4 }, { t: 0.4, v: 12 }, { t: 0.49, v: 22 }, { t: 0.76, v: 34 }],
  backSwingScale: 0.55,
}

/**
 * v4walk: 見本 Z3 (ZZZ 刀を背負った黒髪の少女・真横) の歩き。 1歩 28 コマ (0.47 秒)・片足が床に居るのは 1周の 0.59。
 *   踵から着き (爪先が上がる)・足の裏を着けて体の下を通し・踵を上げて爪先で蹴る。 蹴った足は膝を曲げて床から上げ、前へ振って伸ばす。
 *   腰は片足の真上で一番高く (脚が伸び切る)・両足が着いた所で一番低い。
 */
export const V4_WALK: V4Gait = {
  speedPxPerSec: 110,
  stepSec: 0.45,
  stanceFrac: 0.57,
  contactFwdPx: 21,
  stance: [{ s: 0, foot: -14 }, { s: 0.16, foot: 0 }, { s: 0.55, foot: 0 }, { s: 0.85, foot: 18 }, { s: 1, foot: 34 }],
  // Z3 #4 (爪先が離れる・膝 40°)・#8 (膝 60°・足が床から上がる)・#12 (すれ違い)・#16〜20 (前へ振る)・#24 (伸ばして着地へ)
  swing: [
    { u: 0.12, thigh: -8, knee: 45, pf: 20 },
    { u: 0.30, thigh: 4, knee: 58, pf: 10 },
    { u: 0.52, thigh: 18, knee: 42, pf: 0 },
    { u: 0.74, thigh: 26, knee: 18, pf: -6 },
    { u: 0.90, thigh: 24, knee: 4, pf: -12 },
  ],
  hip: [{ t: 0, v: -40 }, { t: 0.1, v: -35 }, { t: 0.17, v: -10 }, { t: 0.36, v: 4 }, { t: 0.56, v: -6 }, { t: 0.7, v: -23 }, { t: 0.86, v: -23 }, { t: 0.96, v: -40 }],
  backSwingScale: 0.8,
}
